import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsGrid, Minus, Plus, Trash01 } from "@untitledui/icons";
import {
	type CSSProperties,
	type ReactElement,
	useMemo,
	useState,
} from "react";
import { Button as AriaButton } from "react-aria-components";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { AttachmentRequirementTagDisplay } from "@/services/shared/attachment-requirement.types";
import {
	type AttachmentTagDraft,
	attachmentTagsSaveEnabled,
} from "./diff-attachment-tags";

function newDraft(): AttachmentTagDraft {
	return {
		key: crypto.randomUUID(),
		name: "",
		description: "",
		allowedKind: "any",
		minimumCount: 1,
	};
}

function fromTag(tag: AttachmentRequirementTagDisplay): AttachmentTagDraft {
	return {
		key: tag.id,
		persistedId: tag.id,
		name: tag.name,
		description: tag.description ?? "",
		allowedKind: tag.allowedKind,
		minimumCount: tag.minimumCount > 0 ? tag.minimumCount : 1,
	};
}

function SortableTagRow({
	draft,
	onChange,
	onDelete,
}: {
	draft: AttachmentTagDraft;
	onChange: (next: AttachmentTagDraft) => void;
	onDelete: () => void;
}): ReactElement {
	const { t } = useAppTranslation();
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id: draft.key });
	const style: CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
	};
	const minimumCount = Math.max(1, draft.minimumCount);
	const changeQuantity = (next: number) =>
		onChange({ ...draft, minimumCount: Math.max(1, next) });

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="flex items-end gap-3 rounded-lg border border-secondary bg-primary p-3"
		>
			<div className="flex h-10 shrink-0 items-center">
				<button
					type="button"
					className="cursor-grab text-fg-quaternary"
					aria-label={t("admin.attachmentRequirements.modal.reorder")}
					{...attributes}
					{...listeners}
				>
					<DotsGrid className="size-5" />
				</button>
			</div>
			<div className="flex min-w-0 flex-1 flex-wrap items-end gap-3 md:flex-nowrap">
				<Input
					label={t("admin.attachmentRequirements.modal.name")}
					value={draft.name}
					maxLength={50}
					size="md"
					onChange={(value) => onChange({ ...draft, name: value })}
					className="flex min-w-0 flex-1 basis-48 flex-col gap-1.5"
				/>
				<Select
					label={t("admin.attachmentRequirements.modal.fileType")}
					selectedKey={draft.allowedKind}
					size="md"
					onSelectionChange={(key) => {
						if (key === "photo" || key === "file" || key === "any") {
							onChange({ ...draft, allowedKind: key });
						}
					}}
					className="w-40 shrink-0 [&_button]:h-10 [&_button]:py-0"
				>
					<SelectItem id="photo">
						{t("admin.attachmentRequirements.kind.photo")}
					</SelectItem>
					<SelectItem id="file">
						{t("admin.attachmentRequirements.kind.file")}
					</SelectItem>
					<SelectItem id="any">
						{t("admin.attachmentRequirements.kind.any")}
					</SelectItem>
				</Select>
				<div className="flex shrink-0 flex-col gap-1.5">
					<Label elementType="span" isRequired={false}>
						{t("admin.attachmentRequirements.modal.quantity")}
					</Label>
					<span
						data-testid="required-attachments-quantity-stepper"
						className="inline-flex h-10 w-fit items-stretch overflow-hidden rounded-lg border border-primary bg-primary shadow-xs"
					>
						<AriaButton
							aria-label={t(
								"admin.attachmentRequirements.modal.decreaseQuantity",
							)}
							isDisabled={minimumCount <= 1}
							onPress={() => changeQuantity(minimumCount - 1)}
							className="flex h-full w-10 items-center justify-center border-r border-primary text-fg-secondary outline-focus-ring hover:bg-primary_hover focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:text-fg-disabled"
						>
							<Minus className="size-5" aria-hidden />
						</AriaButton>
						<span
							className="flex h-full min-w-12 items-center justify-center px-2 text-md font-medium text-primary"
							aria-live="polite"
						>
							{minimumCount}
						</span>
						<AriaButton
							aria-label={t(
								"admin.attachmentRequirements.modal.increaseQuantity",
							)}
							onPress={() => changeQuantity(minimumCount + 1)}
							className="flex h-full w-10 items-center justify-center border-l border-primary text-fg-secondary outline-focus-ring hover:bg-primary_hover focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:text-fg-disabled"
						>
							<Plus className="size-5" aria-hidden />
						</AriaButton>
					</span>
				</div>
			</div>
			<Button
				color="secondary"
				size="sm"
				iconLeading={Trash01}
				className="size-10 shrink-0"
				aria-label={t("admin.attachmentRequirements.modal.delete")}
				onPress={onDelete}
			/>
		</div>
	);
}

export function ConfigureRequiredAttachmentsModal({
	isOpen,
	tags,
	isSaving,
	onOpenChange,
	onSave,
}: {
	isOpen: boolean;
	tags: AttachmentRequirementTagDisplay[];
	isSaving: boolean;
	onOpenChange: (open: boolean) => void;
	onSave: (drafts: AttachmentTagDraft[]) => void;
}): ReactElement {
	const { t } = useAppTranslation();
	const [drafts, setDrafts] = useState<AttachmentTagDraft[]>(() =>
		tags.length > 0 ? tags.map(fromTag) : [newDraft()],
	);
	const [openedFor, setOpenedFor] = useState(tags);
	if (isOpen && openedFor !== tags) {
		setOpenedFor(tags);
		setDrafts(tags.length > 0 ? tags.map(fromTag) : [newDraft()]);
	}

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
	);
	const ids = useMemo(() => drafts.map((draft) => draft.key), [drafts]);
	const canSave = attachmentTagsSaveEnabled(drafts) && !isSaving;

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		setDrafts((current) => {
			const oldIndex = current.findIndex((draft) => draft.key === active.id);
			const newIndex = current.findIndex((draft) => draft.key === over.id);
			if (oldIndex < 0 || newIndex < 0) return current;
			return arrayMove(current, oldIndex, newIndex);
		});
	};

	return (
		<ModalOverlay
			isOpen={isOpen}
			isDismissable={!isSaving}
			onOpenChange={onOpenChange}
		>
			<Modal className="max-w-3xl">
				<Dialog className="w-full">
					<div className="flex w-full flex-col gap-4 rounded-xl bg-primary p-6 shadow-xl">
						<h2 className="text-lg font-semibold text-primary">
							{t("admin.attachmentRequirements.modal.title")}
						</h2>
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={ids}
								strategy={verticalListSortingStrategy}
							>
								<div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
									{drafts.map((draft) => (
										<SortableTagRow
											key={draft.key}
											draft={draft}
											onChange={(next) =>
												setDrafts((current) =>
													current.map((row) =>
														row.key === draft.key ? next : row,
													),
												)
											}
											onDelete={() =>
												setDrafts((current) =>
													current.filter((row) => row.key !== draft.key),
												)
											}
										/>
									))}
								</div>
							</SortableContext>
						</DndContext>
						<div>
							<Button
								color="secondary"
								size="sm"
								iconLeading={Plus}
								className="h-10"
								onPress={() => setDrafts((current) => [...current, newDraft()])}
							>
								{t("admin.attachmentRequirements.modal.add")}
							</Button>
						</div>
						<div className="flex gap-3">
							<Button
								color="secondary"
								size="md"
								isDisabled={isSaving}
								onPress={() => onOpenChange(false)}
								className="h-10 flex-1"
							>
								{t("admin.attachmentRequirements.modal.cancel")}
							</Button>
							<Button
								color="primary"
								size="md"
								isDisabled={!canSave}
								isLoading={isSaving}
								onPress={() => onSave(drafts)}
								className="h-10 flex-1"
							>
								{t("admin.attachmentRequirements.modal.save")}
							</Button>
						</div>
					</div>
				</Dialog>
			</Modal>
		</ModalOverlay>
	);
}
